import { useEffect, useState } from 'react';
import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { InputNumber } from 'antd';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { FilterPanel } from './FilterPanel';

type NumberRangeFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  min?: number;
  max?: number;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  width?: number;
};

/** Numeric range column filter, applied as `field[gte]` / `field[lte]`. */
export const NumberRangeFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  min,
  max,
  fromPlaceholder,
  toPlaceholder,
  width = 140,
}: NumberRangeFilterProps) => {
  const t = useTranslate();
  const currentFrom = getFilterValue<number>(filters, field, 'gte');
  const currentTo = getFilterValue<number>(filters, field, 'lte');
  const [from, setFrom] = useState<number | null>(currentFrom ?? null);
  const [to, setTo] = useState<number | null>(currentTo ?? null);

  useEffect(() => setFrom(currentFrom ?? null), [currentFrom]);
  useEffect(() => setTo(currentTo ?? null), [currentTo]);

  const handleFilter = () => {
    setFilters(
      [
        { field, operator: 'gte', value: from ?? undefined },
        { field, operator: 'lte', value: to ?? undefined },
      ],
      'merge'
    );
    confirm();
  };

  const handleReset = () => {
    setFrom(null);
    setTo(null);
    setFilters(
      [
        { field, operator: 'gte', value: undefined },
        { field, operator: 'lte', value: undefined },
      ],
      'merge'
    );
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <InputNumber
        min={min}
        max={max}
        placeholder={fromPlaceholder ?? t('base.filter.from', 'Od')}
        value={from}
        onChange={(v) => setFrom(v)}
        style={{ width }}
      />
      <InputNumber
        min={min}
        max={max}
        placeholder={toPlaceholder ?? t('base.filter.to', 'Do')}
        value={to}
        onChange={(v) => setTo(v)}
        style={{ width }}
      />
    </FilterPanel>
  );
};
